// =====================================================================================
// INVRT Radar - web console - session + org scope
//
// Radar has NO passwords. Sign-in is a magic link and nothing else, so this provider
// exposes exactly one credential-taking call: sendMagicLink(email).
//
// Org scope lives here too, because the two are inseparable in the API contract: every
// authenticated call carries the session JWT AND an X-Org-Id the server verifies against
// `memberships`. The chosen org id is remembered in localStorage between visits and
// re-validated against the memberships actually returned - a stale id from a revoked
// membership is discarded rather than sent.
// =====================================================================================

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { Session, SupabaseClient } from "@supabase/supabase-js";
import { getSupabase } from "@/lib/supabase";
import { authRedirectUrl } from "@/lib/supabase";
import { dedupeMembershipsByOrgId } from "@/lib/memberships";
import type { Membership, Profile } from "@/lib/types";

const ORG_STORAGE_KEY = "radar.org_id";

export interface SessionContextValue {
  supabase: SupabaseClient;
  session: Session | null;
  /** True until the first auth state has been resolved. */
  loading: boolean;
  memberships: Membership[];
  membershipsLoading: boolean;
  /** True once memberships have been read at least once for the current user. Guards the
   * zero-membership claim routing (W6-03-B) from firing on the not-yet-loaded frame. */
  membershipsLoaded: boolean;
  membershipsError: Error | null;
  /** Re-reads memberships (and the profile flag) on demand - the claim page calls this
   * after a successful first-membership claim so the OrgSwitcher populates (W6-03-B). */
  refreshMemberships: () => Promise<void>;
  subscribeScopeInvalidation: (listener: () => void) => () => void;
  orgId: string | null;
  setOrgId: (orgId: string) => void;
  /** The membership row for the chosen org, if there is one. */
  activeMembership: Membership | null;
  /** Mirrors profiles.is_super_admin. The SERVER enforces it; this only shows or hides. */
  isSuperAdmin: boolean;
  sendMagicLink: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

function readStoredOrgId(): string | null {
  try {
    return window.localStorage.getItem(ORG_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStoredOrgId(orgId: string): void {
  try {
    window.localStorage.setItem(ORG_STORAGE_KEY, orgId);
  } catch {
    // A browser with storage blocked still works; the choice just does not persist.
  }
}

export function SessionProvider({
  children,
  client,
}: {
  children: ReactNode;
  /** Test seam. Production passes nothing and the shared browser client is used. */
  client?: SupabaseClient;
}) {
  const supabase = useMemo(() => client ?? getSupabase(), [client]);

  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [membershipsLoading, setMembershipsLoading] = useState(false);
  const [membershipsLoaded, setMembershipsLoaded] = useState(false);
  const [membershipsError, setMembershipsError] = useState<Error | null>(null);
  const [orgId, setOrgIdState] = useState<string | null>(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  // Observation only: these values never authorise a request or replace provider state.
  const scopeObserver = useMemo(() => ({
    client: supabase,
    listeners: new Set<() => void>(),
    scope: { principal: null as string | null, login: "", org: null as string | null, status: null as string | null },
    memberships: [] as Membership[],
  }), [supabase]);
  const notifyScopeInvalidation = useCallback(() => {
    for (const listener of [...scopeObserver.listeners]) {
      if (!scopeObserver.listeners.has(listener)) continue;
      try { listener(); }
      catch { scopeObserver.listeners.delete(listener); console.warn("Scope observer failed"); }
    }
  }, [scopeObserver]);
  const observeScope = useCallback((update: Partial<typeof scopeObserver.scope>) => {
    const previous = scopeObserver.scope;
    const next = { ...previous, ...update };
    scopeObserver.scope = next;
    if (previous.principal !== next.principal || previous.login !== next.login || previous.org !== next.org || previous.status !== next.status) notifyScopeInvalidation();
  }, [scopeObserver, notifyScopeInvalidation]);
  const observeOrg = useCallback((next: string | null) => {
    observeScope({ org: next, status: scopeObserver.memberships.find(row => row.org_id === next)?.organizations?.status ?? null });
  }, [observeScope, scopeObserver]);
  const subscribeScopeInvalidation = useCallback((listener: () => void) => {
    scopeObserver.listeners.add(listener);
    return () => { scopeObserver.listeners.delete(listener); };
  }, [scopeObserver]);


  useEffect(() => {
    let active = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      observeScope({ principal: data.session?.user.id ?? null, login: data.session?.user.last_sign_in_at ?? "" });
      setSession(data.session);
      setLoading(false);
    });

    const { data: subscription } = supabase.auth.onAuthStateChange((_event, next) => {
      if (!active) return;
      observeScope({ principal: next?.user.id ?? null, login: next?.user.last_sign_in_at ?? "" });
      setSession(next);
      setLoading(false);
    });

    return () => {
      active = false;
      subscription.subscription.unsubscribe();
      notifyScopeInvalidation();
      scopeObserver.listeners.clear();
    };
  }, [supabase, observeScope, notifyScopeInvalidation, scopeObserver]);

  const userId = session?.user.id ?? null;

  // Superseded loads must not apply their results: each call takes a generation number,
  // and only the latest one may set state - the same stale-response protection the old
  // effect-local `active` flag gave, now shared with on-demand refreshes (W6-03-B).
  const loadGeneration = useRef(0);

  const loadMemberships = useCallback(async (): Promise<void> => {
    if (!userId) return;
    const generation = ++loadGeneration.current;
    setMembershipsLoading(true);
    setMembershipsError(null);

    // memberships is the only source of org scope (migration 0002). The join is read
    // through the user's own RLS-scoped session, so what comes back IS what this user
    // may act as.
    const membershipQuery = supabase
      .from("memberships")
      .select("org_id, is_org_admin, organizations(id, name, slug, tenant_class, status)")
      .order("org_id", { ascending: true });

    // profiles_self_select permits exactly one row: the caller's own.
    const profileQuery = supabase
      .from("profiles")
      .select("id, is_super_admin")
      .eq("id", userId)
      .maybeSingle();

    const [membershipResult, profileResult] = await Promise.all([membershipQuery, profileQuery]);
    if (generation !== loadGeneration.current) return;

    if (membershipResult.error) {
      scopeObserver.memberships = [];
      observeOrg(scopeObserver.scope.org);
      setMembershipsError(new Error(membershipResult.error.message));
      setMemberships([]);
    } else {
      const rows = dedupeMembershipsByOrgId((membershipResult.data ?? []) as unknown as Membership[]);
      scopeObserver.memberships = rows;
      setMemberships(rows);

      // Re-validate any remembered choice: a membership can be revoked between visits,
      // and sending a stale id would only earn a 403. The tenant scope guard
      // (PRD_RESELLER_RESET_2026-10-08, R7) narrows that further: only a platform
      // super-admin may have a remembered organisation honoured at all. Everyone else is
      // pinned to their first membership in stable org_id order, and an unreadable profile
      // fails closed to the pin. The server verifies the header on every request either
      // way; this only stops the console asking for another org's scope.
      const superAdminProfile = !profileResult.error &&
        (profileResult.data as Profile | null)?.is_super_admin === true;
      const stored = superAdminProfile ? readStoredOrgId() : null;
      const valid = rows.find((row) => row.org_id === stored) ?? rows[0] ?? null;
      observeOrg(valid?.org_id ?? null);
      setOrgIdState(valid?.org_id ?? null);
    }

    if (!profileResult.error) {
      setIsSuperAdmin((profileResult.data as Profile | null)?.is_super_admin === true);
    }
    setMembershipsLoaded(true);
    setMembershipsLoading(false);
  }, [supabase, userId, scopeObserver, observeOrg]);

  useEffect(() => {
    if (!userId) {
      loadGeneration.current++; // invalidate any load still in flight for a signed-out user
      scopeObserver.memberships = [];
      observeOrg(null);
      setMemberships([]);
      setOrgIdState(null);
      setIsSuperAdmin(false);
      setMembershipsLoaded(false);
      return;
    }

    void loadMemberships();
  }, [userId, loadMemberships, scopeObserver, observeOrg]);

  const setOrgId = useCallback((next: string) => {
    observeOrg(next);
    setOrgIdState(next);
    writeStoredOrgId(next);
  }, [observeOrg]);

  const sendMagicLink = useCallback(
    async (email: string) => {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: authRedirectUrl() },
      });
      if (error) throw new Error(error.message);
    },
    [supabase],
  );

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    observeOrg(null);
    setOrgIdState(null);
  }, [supabase, observeOrg]);

  const activeMembership = useMemo(
    () => memberships.find((row) => row.org_id === orgId) ?? null,
    [memberships, orgId],
  );

  const value = useMemo<SessionContextValue>(
    () => ({
      supabase,
      session,
      loading,
      memberships,
      membershipsLoading,
      membershipsLoaded,
      membershipsError,
      refreshMemberships: loadMemberships,
      subscribeScopeInvalidation,
      orgId,
      setOrgId,
      activeMembership,
      isSuperAdmin,
      sendMagicLink,
      signOut,
    }),
    [
      supabase,
      session,
      loading,
      memberships,
      membershipsLoading,
      membershipsLoaded,
      membershipsError,
      loadMemberships,
      subscribeScopeInvalidation,
      orgId,
      setOrgId,
      activeMembership,
      isSuperAdmin,
      sendMagicLink,
      signOut,
    ],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const context = useContext(SessionContext);
  if (!context) throw new Error("useSession must be used inside a SessionProvider.");
  return context;
}
